import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LogInModal from 'flarum/forum/components/LogInModal';
import extractText from 'flarum/common/utils/extractText';

/**
 * `/invitacion#<token>` — quien recibe el enlace (T12B · objetivo 3).
 *
 * --- NO SE CANJEA AL CARGAR ---
 *
 * La página sólo enseña; entrar al grupo exige pulsar un botón. Es la misma regla que la
 * confirmación de correo de T12, y por el mismo motivo: un GET con efectos lo dispara cualquier
 * escáner de enlaces, precargador o antivirus del correo del invitado, y la invitación se
 * gastaría antes de que la persona la abriera.
 *
 * --- EL TOKEN SE GUARDA SI HACE FALTA REGISTRARSE ---
 *
 * Sin sesión no hay a quién atribuir la entrada, así que el canje exige cuenta. Para no perder
 * la invitación por el camino, el token queda en `sessionStorage` y esta misma página lo recoge
 * al volver. `sessionStorage` y no `localStorage`: es una credencial de un solo salto y no tiene
 * por qué sobrevivir al cierre de la pestaña.
 *
 * --- Y TAMBIÉN EN EL SERVIDOR, DESDE PKG-FORO-INVITE-AUTOGRANT ---
 *
 * `sessionStorage` deja de ser la ÚNICA fuente. En cuanto hay sesión, la intención se sella en el
 * servidor (digerida, nunca el token), y eso es lo que permite cumplir la promesa que antes se
 * rompía: confirmar el correo abre OTRA pestaña, la de aquí se pierde, y la invitación se perdía
 * con ella. Ahora el servidor se acuerda y remata el canje al confirmar.
 *
 * La copia del navegador se queda: es lo único que sobrevive al salto invitado → `/registro` →
 * vuelta, cuando todavía no existe cuenta a la que sellarle nada.
 */
const STASH_KEY = 'umbral.invite.token';

/**
 * Marca de que quien vuelve a esta página venía de pulsar «Iniciar sesión» AQUÍ.
 *
 * Es lo que separa la situación 2 («entra solo tras el login») de la situación 1 («con sesión, un
 * botón»): sin esta marca, una recarga cualquiera con sesión canjearía sin que nadie lo pidiera,
 * que es precisamente lo que la página lleva desde T12B negándose a hacer.
 */
const LOGIN_INTENT_KEY = 'umbral.invite.login-intent';

export default class InvitacionPage extends Page {
  /** 'idle' | 'joining' | 'joined' | 'error' */
  state = 'idle';

  /** Código del error del servidor, para decir cuál de los «no» es. */
  error: string | null = null;

  inviter: string | null = null;

  /** El canje unificado indica si era invitación personal, evento o campaña. */
  source: 'invite' | 'access_code' | null = null;

  channel: 'event' | 'admin_campaign' | null = null;

  accessTitle: string | null = null;

  /** Fallback for browsers that deny sessionStorage; never leaves this component instance. */
  ephemeralToken: string | null = null;

  /** Whether the bearer is safe to remove from the current URL across an auth navigation. */
  tokenStashed = false;

  /** Volvió del login con la cuenta sin confirmar: no se canjea, se explica. */
  afterLoginUnconfirmed = false;

  oninit(vnode: any) {
    super.oninit(vnode);

    app.setTitle(extractText(this.t('meta_title')));
    app.setTitleCount(0);

    // El fragmento nunca viaja al servidor. Se guarda para atravesar login/alta y se elimina
    // de la barra de direcciones antes de que cualquier navegación pueda conservarlo.
    const fromRoute = this.fragmentToken() || this.validToken(m.route.param('token') || '');

    if (fromRoute) {
      this.ephemeralToken = fromRoute;

      try {
        sessionStorage.setItem(STASH_KEY, fromRoute);
        this.tokenStashed = sessionStorage.getItem(STASH_KEY) === fromRoute;
      } catch (e) {
        // Navegación privada o almacenamiento lleno: el fragmento queda en la URL para
        // sobrevivir el registro o la recarga que hace el login. Nunca llega al servidor.
      }

      if (this.tokenStashed) {
        this.clearBearerFromLocation();
      } else {
        // También transforma inmediatamente el path legado: un reload de login puede repetir
        // la petición, pero el fragmento nunca se reenvía al servidor.
        this.preserveBearerInFragment(fromRoute);
      }
    }

    this.resumeAfterAuth();
  }

  /**
   * Lo que ocurre al llegar aquí CON sesión (PKG-FORO-INVITE-AUTOGRANT).
   *
   * Dos cosas distintas, y conviene no confundirlas:
   *
   *   · SELLAR es incondicional. Le pasa a cualquiera con sesión y token a la vista, y no gasta
   *     ni concede nada: sólo deja dicho en el servidor «esta persona venía con esta invitación»,
   *     para que confirmar el correo —que ocurre en otra pestaña— pueda rematarla.
   *   · CANJEAR sólo ocurre si quien vuelve acaba de iniciar sesión DESDE AQUÍ. Es un gesto
   *     explícito sobre esta página, no una carga cualquiera: sin esa distinción, recargar con
   *     sesión canjearía sola y la situación 1 (con sesión, un botón) dejaría de existir.
   */
  resumeAfterAuth(): void {
    if (!app.session.user || !this.token()) return;

    this.stashPending();

    if (!this.consumeLoginIntent()) return;

    if (this.emailConfirmed()) {
      this.redeem();
      return;
    }

    // El pendiente ya está sellado arriba, así que la promesa se puede cumplir: no hay que
    // volver aquí, basta con confirmar el correo.
    this.afterLoginUnconfirmed = true;
  }

  /**
   * Sella la intención en el servidor. Deliberadamente sin manejar el resultado: es una mejora
   * de resiliencia, no un paso del que dependa nada de esta pantalla — si falla, el botón de
   * canjear sigue estando y el token sigue en la pestaña.
   */
  stashPending(): void {
    const token = this.token();

    if (!token) return;

    app
      .request<any>({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/umbral/invites/pending',
        body: { data: { attributes: { token } } },
        errorHandler: () => {},
      })
      .then(
        () => {},
        () => {}
      );
  }

  /** `true` una sola vez: la marca se consume para que una recarga posterior no reincida. */
  consumeLoginIntent(): boolean {
    try {
      if (sessionStorage.getItem(LOGIN_INTENT_KEY) !== '1') return false;

      sessionStorage.removeItem(LOGIN_INTENT_KEY);

      return true;
    } catch (e) {
      return false;
    }
  }

  markLoginIntent(): void {
    try {
      sessionStorage.setItem(LOGIN_INTENT_KEY, '1');
    } catch (e) {
      // Almacenamiento denegado: se degrada a la situación 1 —el botón—, que es el camino que
      // ya funcionaba. Nunca a un callejón.
    }
  }

  /**
   * `isEmailConfirmed` sólo viaja en el payload para uno mismo y para el admin; si no está, se
   * intenta y manda el servidor, que es quien decide de verdad. Misma convención que
   * `RegistroPage`.
   *
   * NO ES el `emailConfirmed()` compartido de `utils/routeState` — y la diferencia es el
   * invitado: aquél devuelve `false` sin sesión (sus dos páginas ya descartaron ese caso antes
   * de preguntar), y éste devuelve `true` (`!user ||`), porque a `/invitacion` se llega SIN
   * cuenta con toda naturalidad y ahí «no hay sesión» no es «correo sin confirmar».
   */
  emailConfirmed(): boolean {
    const user: any = app.session.user;

    return !user || typeof user.isEmailConfirmed !== 'function' || user.isEmailConfirmed();
  }

  token(): string | null {
    if (this.ephemeralToken) return this.ephemeralToken;

    try {
      return sessionStorage.getItem(STASH_KEY);
    } catch (e) {
      return null;
    }
  }

  fragmentToken(): string | null {
    const raw = window.location.hash.replace(/^#/, '');

    if (!raw) return null;

    try {
      return this.validToken(decodeURIComponent(raw));
    } catch (e) {
      return this.validToken(raw);
    }
  }

  validToken(value: string): string | null {
    return /^[A-Za-z0-9_-]{32}$/.test(value) ? value : null;
  }

  clearBearerFromLocation(): void {
    window.history.replaceState(window.history.state, document.title, app.route('umbral-comunidad.invitacion'));
  }

  preserveBearerInFragment(token: string): void {
    window.history.replaceState(window.history.state, document.title, app.route('umbral-comunidad.invitacion') + '#' + encodeURIComponent(token));
  }

  registrationUrl(): string {
    const token = this.token();

    return app.route('umbral-comunidad.registro') + (!this.tokenStashed && token ? '#' + encodeURIComponent(token) : '');
  }

  goToRegistration(event: Event): void {
    if (this.tokenStashed) return;

    event.preventDefault();
    window.location.replace(this.registrationUrl());
  }

  view() {
    return (
      <div className="CmInvite-container container">
        <div className="CmInvite" data-cm-invite={this.state}>
          {this.body()}
        </div>
      </div>
    );
  }

  body() {
    if (this.state === 'joined') return this.done();
    if (!app.session.user) return this.guest();

    return this.ready();
  }

  /** Sin sesión: el canje necesita cuenta, y se dice por qué. */
  guest() {
    return [
      <span className="CmInvite-ic">✉</span>,
      <h1 className="CmInvite-title">{this.t('guest_title_unified')}</h1>,
      <p className="CmInvite-sub">{this.t('guest_body_unified')}</p>,
      <Link
        className="Button Button--primary CmInvite-cta"
        external={!this.tokenStashed}
        href={this.registrationUrl()}
        onclick={(event: Event) => this.goToRegistration(event)}
      >
        {this.t('guest_cta')}
      </Link>,
      <Button
        className="Button Button--link CmInvite-secondary"
        onclick={() => {
          // La marca se deja ANTES de abrir el modal: el login del core recarga la página, y al
          // volver es lo único que distingue «vengo de identificarme aquí» de una visita normal.
          this.markLoginIntent();
          app.modal.show(LogInModal);
        }}
      >
        {this.t('guest_login')}
      </Button>,
    ];
  }

  /** Con sesión: un botón, y nada ocurre hasta pulsarlo. */
  ready() {
    return [
      <span className="CmInvite-ic">🔒</span>,
      <h1 className="CmInvite-title">{this.t('ready_title_unified')}</h1>,
      <p className="CmInvite-sub">{this.t('ready_body_unified')}</p>,

      // Entró bien pero le falta confirmar el correo. No es un error y no se pinta como tal: es
      // la promesa de que no hay que volver aquí, que es justo lo que el servidor ya tiene
      // sellado en este punto.
      this.afterLoginUnconfirmed ? (
        <p className="CmInvite-sub" data-cm-invite-pending="1">
          {this.t('after_login_unconfirmed')}
        </p>
      ) : null,

      <Button
        className="Button Button--primary CmInvite-cta"
        data-cm-redeem="1"
        loading={this.state === 'joining'}
        disabled={this.state === 'joining'}
        onclick={() => this.redeem()}
      >
        {this.t('ready_cta_unified')}
      </Button>,

      this.error ? (
        <p className="CmInvite-error" data-cm-invite-error={this.error}>
          {this.t('error_' + this.error)}
        </p>
      ) : null,
    ];
  }

  /** M4c — celebración corta y una sola acción. */
  done() {
    const viaCode = this.source === 'access_code';

    return [
      <span className="CmInvite-ic CmInvite-ic--ok">✓</span>,
      <h1 className="CmInvite-title">{this.t('done_title')}</h1>,
      <p className="CmInvite-sub">
        {viaCode
          ? this.t(this.channel === 'admin_campaign' ? 'done_body_campaign' : 'done_body_event', { title: this.accessTitle || '' })
          : this.inviter
          ? this.t('done_body_by', { name: this.inviter })
          : this.t('done_body')}
      </p>,
      <Link className="Button Button--primary CmInvite-cta" href={app.route('umbral-comunidad.discussions')}>
        {this.t('done_cta')}
      </Link>,
    ];
  }

  redeem() {
    const token = this.token();

    if (!token) {
      this.error = 'not_found';
      m.redraw();
      return;
    }

    this.state = 'joining';
    this.error = null;
    m.redraw();

    app
      .request<any>({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/umbral/invites/redeem',
        body: { data: { attributes: { token } } },
        errorHandler: (e: any) => {
          this.state = 'idle';
          // El código del servidor decide el mensaje: agotada, caducada, revocada y «ya eres
          // miembro» llevan a acciones distintas, y un «no válido» genérico dejaría a la
          // persona sin saber a quién escribir (estado M4 del Spec §6).
          this.error = this.codeOf(e);
          m.redraw();
        },
      })
      .then(
        (result: any) => {
          this.state = 'joined';
          this.inviter = result && result.inviter;
          this.source = result && result.source === 'access_code' ? 'access_code' : 'invite';
          this.channel = result && ['event', 'admin_campaign'].includes(result.channel) ? result.channel : null;
          this.accessTitle = (result && result.title) || null;

          try {
            sessionStorage.removeItem(STASH_KEY);
          } catch (e) {
            // Nada que hacer: el token ya está gastado en el servidor.
          }
          this.ephemeralToken = null;
          this.clearBearerFromLocation();

          // La sesión ahora pertenece a un grupo más. Sin recargar los datos del usuario, el
          // resto de la SPA (la n8card, las categorías visibles) seguiría creyendo que no.
          if (app.session.user) {
            app.store.find('users', app.session.user.id()!).catch(() => {});
          }

          m.redraw();
        },
        // El core hace `Promise.reject` DESPUÉS de llamar al `errorHandler`, así que sin esta
        // rama queda un «unhandled rejection» en cada error. Mismo caso que T12.
        () => {}
      );
  }

  /**
   * Código de error del cuerpo JSON:API, acotado a los que esta pantalla sabe explicar.
   *
   * Un código desconocido cae en `not_found` en vez de pintarse crudo: enseñar
   * `umbral_invite_algo` al invitado no le dice nada y además filtra nombres internos.
   */
  codeOf(e: any): string {
    const raw = String(e?.response?.errors?.[0]?.code || '');
    const known = [
      'not_found',
      'revoked',
      'expired',
      'exhausted',
      'already_member',
      'self',
      'not_configured',
      'forbidden',
      'unconfirmed',
      'not_started',
      'paused',
      'rate_limited',
      'access_degraded',
    ];
    const code =
      raw === 'umbral_rate_limited'
        ? 'rate_limited'
        : raw === 'umbral_n8n_access_degraded'
        ? 'access_degraded'
        : raw.replace(/^umbral_(?:invite|access_code)_/, '');

    return known.includes(code) ? code : 'not_found';
  }

  t(key: string, params?: any) {
    return app.translator.trans('umbral-comunidad.forum.invitacion.' + key, params);
  }
}
